import localeUtils from 'utils/LanguageLocale';

export function removeItemsWithAllLabel(navParentItems) {
    // Deep clone the input object to ensure immutability
    const navItemsCopy = JSON.parse(JSON.stringify(navParentItems));

    // Regex: matches 'all' or 'all ...' (case-insensitive, start of string)
    const stringMatchRegex = /^all( |$)/i;

    if (!navItemsCopy.items || !Array.isArray(navItemsCopy.items)) {
        return navItemsCopy;
    }

    navItemsCopy.items = navItemsCopy.items.filter(navItem => {
        if (!navItem || !Array.isArray(navItem.items) || !navItem.items?.length) {
            return true;
        }

        navItem.items = navItem.items.filter(childrenItem => {
            if (!childrenItem || !Array.isArray(childrenItem.items) || !childrenItem.items?.length) {
                return true;
            }

            childrenItem.items = childrenItem.items.filter(subChildrenItem => {
                if (!subChildrenItem.label || typeof subChildrenItem.label !== 'string') {
                    return true;
                }

                // Remove if label matches regex
                return !stringMatchRegex.test(subChildrenItem.label.trim());
            });

            return childrenItem.items?.length > 0;
        });

        return navItem.items?.length > 0;
    });

    return navItemsCopy;
}

// The meganav sale item is labelled differently across surfaces and locales.
// Desktop (FS) uses the long form; the mobile (SMUI/MW) nav uses the short form.
const SALE_LABELS = {
    desktop: {
        en: 'Sale & Offers',
        fr: 'Soldes et offres'
    },
    smui: {
        en: 'Sale',
        fr: 'Soldes'
    }
};
const SALE_TARGET_POSITION = 1;

/**
 * @returns {boolean} Whether the current surface is SMUI (mobile web).
 */
function isSMUI() {
    return typeof Sephora !== 'undefined' && typeof Sephora.isMobile === 'function' && Sephora.isMobile();
}

/**
 * Resolves the label used to identify the sale item for the current surface and
 * locale. SMUI (mobile web) is detected via `Sephora.isMobile()`.
 *
 * @returns {string} The sale item label to match against.
 */
function getSaleLabel() {
    const labels = isSMUI() ? SALE_LABELS.smui : SALE_LABELS.desktop;

    return localeUtils.isFrench() ? labels.fr : labels.en;
}

/**
 * Reorders the top-level meganav items based on the OptiMegaSaleOffers A/B test.
 *
 * The active experience is surfaced on the `Sephora.optiMegaSaleOffers` global
 * (set server-side from the rcps_OptiMegaSaleOffers cookie), so this runs
 * consistently during SSR and after hydration. Control users (no experience)
 * receive the original order untouched.
 *
 * The sale item is identified by label (case-insensitive), resolved for the
 * current surface and locale (see `getSaleLabel`): desktop matches
 * 'Sale & Offers'/'Soldes et offres' while SMUI matches 'Sale'/'Soldes'. A
 * navigable (link-bearing) match is preferred, because the meganav can contain
 * non-navigable placeholder items that share the sale label and never render;
 * it falls back to the first label match so a link-less dropdown parent (desktop)
 * still reorders.
 *
 * For both challengers the sale item is moved to the 2nd position.
 * For `challengerTwo` that item is additionally flagged with
 * `isSaleOffersHighlighted` so the consuming component can highlight it — desktop
 * renders a red background, SMUI renders the label font in red.
 *
 * @param {Array} items - Top-level meganav items in their default order.
 * @returns {Array} Reordered items (new array) or the original when no test applies.
 */
export function sortMegaNavItems(items) {
    const experience = typeof Sephora !== 'undefined' && Sephora.optiMegaSaleOffers;

    if (!Array.isArray(items) || items.length === 0) {
        return items;
    }

    if (experience !== 'challengerOne' && experience !== 'challengerTwo') {
        return items;
    }

    const saleLabel = getSaleLabel().toLowerCase();
    const matchesSaleLabel = item => item?.label?.toLowerCase() === saleLabel;

    // Prefer a navigable (link-bearing) match: some surfaces (e.g. SMUI) include a
    // link-less placeholder that shares the sale label and never renders as a
    // chiclet, so moving it would leave the visible order unchanged. Fall back to
    // the first label match so surfaces whose sale item is a link-less dropdown
    // parent (e.g. desktop top nav) still reorder.
    let saleIndex = items.findIndex(item => matchesSaleLabel(item) && !!item?.link);

    if (saleIndex === -1) {
        saleIndex = items.findIndex(matchesSaleLabel);
    }

    if (saleIndex === -1) {
        return items;
    }

    const sortedItems = [...items];
    const [saleItem] = sortedItems.splice(saleIndex, 1);

    if (experience === 'challengerTwo') {
        // Clone the item so the highlight flag doesn't mutate shared CMS/Redux state.
        sortedItems.splice(SALE_TARGET_POSITION, 0, {
            ...saleItem,
            isSaleOffersHighlighted: true
        });
    } else {
        sortedItems.splice(SALE_TARGET_POSITION, 0, saleItem);
    }

    return sortedItems;
}
